const filterRoots = document.querySelectorAll<HTMLElement>(
  "[data-projects-filter]",
);

for (let root of filterRoots) {
  let industryQuery = root.dataset.industryQuery;
  let categoryQuery = root.dataset.categoryQuery;
  let paginationQuery = root.dataset.paginationQuery;
  let forms = Array.from(
    root.querySelectorAll<HTMLFormElement>("[data-project-filter-form]"),
  );
  let activeRequest: AbortController | null = null;

  if (!industryQuery || !categoryQuery || !paginationQuery) continue;

  function queryFieldName(queryName: string): string {
    return `${queryName}[]`;
  }

  function checkboxes(queryName: string): HTMLInputElement[] {
    return Array.from(
      root.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'),
    ).filter((input) => input.name === queryFieldName(queryName));
  }

  function selectedCount(queryName: string): number {
    return new Set(
      checkboxes(queryName)
        .filter((input) => input.checked)
        .map((input) => input.value),
    ).size;
  }

  function updateCountBadges(): void {
    let industryCount = selectedCount(industryQuery);
    let categoryCount = selectedCount(categoryQuery);
    let counts = {
      all: industryCount + categoryCount,
      industry: industryCount,
      category: categoryCount,
    };

    for (let [kind, count] of Object.entries(counts)) {
      for (let badge of root.querySelectorAll<HTMLElement>(
        `[data-project-filter-count="${kind}"]`,
      )) {
        badge.textContent = String(count);
        badge.classList.toggle("hidden", count === 0);
      }
    }
  }

  function syncDuplicateCheckboxes(source: HTMLInputElement): void {
    for (let input of root.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"]',
    )) {
      if (input !== source && input.name === source.name && input.value === source.value) {
        input.checked = source.checked;
      }
    }
  }

  function requestUrl(form: HTMLFormElement): URL {
    let url = new URL(window.location.href);

    for (let queryName of [industryQuery, categoryQuery]) {
      url.searchParams.delete(queryName);
      url.searchParams.delete(queryFieldName(queryName));
    }
    url.searchParams.delete(paginationQuery);

    for (let [name, value] of new FormData(form)) {
      if (typeof value === "string") url.searchParams.append(name, value);
    }

    url.hash = root.id;
    return url;
  }

  async function filterProjects(form: HTMLFormElement): Promise<void> {
    activeRequest?.abort();
    let request = new AbortController();
    activeRequest = request;
    let url = requestUrl(form);
    let status = root.querySelector<HTMLElement>("[data-project-filter-status]");

    root.setAttribute("aria-busy", "true");
    try {
      let response = await fetch(url, {
        headers: { "X-Requested-With": "fetch" },
        signal: request.signal,
      });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);

      let html = await response.text();
      let page = new DOMParser().parseFromString(html, "text/html");
      let responseRoot = page.getElementById(root.id);
      let currentResults = root.querySelector<HTMLElement>("[data-project-results]");
      let responseResults = responseRoot?.querySelector<HTMLElement>(
        "[data-project-results]",
      );
      let currentActiveFilters = root.querySelector<HTMLElement>(
        "[data-project-active-filters]",
      );
      let responseActiveFilters = responseRoot?.querySelector<HTMLElement>(
        "[data-project-active-filters]",
      );

      if (!responseRoot || !currentResults || !responseResults) {
        throw new Error("Filtered project markup is missing");
      }

      currentResults.replaceWith(document.importNode(responseResults, true));
      if (currentActiveFilters && responseActiveFilters) {
        currentActiveFilters.replaceWith(
          document.importNode(responseActiveFilters, true),
        );
      }

      window.history.replaceState({}, "", url);
      if (status) status.textContent = "Projecten bijgewerkt";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      window.location.assign(url);
    } finally {
      if (activeRequest === request) {
        activeRequest = null;
        root.removeAttribute("aria-busy");
      }
    }
  }

  for (let form of forms) {
    form.addEventListener("change", (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.type !== "checkbox") return;

      syncDuplicateCheckboxes(event.target);
      updateCountBadges();
      void filterProjects(form);
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void filterProjects(form);
    });
  }
}
